import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Gift, ShieldCheck, Upload } from "lucide-react";

const SHIPPING_FEE = 5;
const METHOD_LABEL = { mbway: "MBWay", paypal: "PayPal", transfer: "Transferência Bancária", crypto: "Crypto" };

export default function Premios() {
  const { user, refresh, login } = useAuth();
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prizes");
      setPrizes(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const balance = user?.voucher_balance ?? 0;

  const canRedeem = (price) => user && balance >= price - 0.0001;

  const doRedeem = async (prize, payload) => {
    const r = await api.post(`/prizes/${prize.prize_id}/redeem`, payload);
    await refresh();
    load();
    return r.data;
  };

  const redeem = (prize) => {
    if (!user) { login(); return; }
    setRedeeming(prize);
  };

  return (
    <main className="pt-28 pb-20 max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-wrap items-end justify-between mb-10 gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Loja</p>
          <h1 className="font-display text-5xl md:text-6xl">Prémios de Gaia</h1>
          <p className="text-[color:var(--text-2)] mt-2 max-w-xl">Troca o teu saldo por prémios reais. Podes resgatar quantos couberem no saldo — o resto fica disponível para a próxima troca.</p>
        </div>
        {user && (
          <div className="glass px-6 py-4 text-right">
            <div className="text-xs uppercase text-[color:var(--text-3)]">O teu saldo</div>
            <div className="font-display text-4xl text-gold flex items-center gap-2"><Gift size={22}/> {balance.toFixed(2)}€</div>
          </div>
        )}
      </div>

      {loading ? <div className="text-gold">A carregar…</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {prizes.map(p => {
            const allowed = canRedeem(p.price_eur);
            return (
              <div key={p.prize_id} className="glass overflow-hidden flex flex-col" data-testid={`prize-${p.prize_id}`}>
                {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-44 object-cover" />}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display text-2xl">{p.name}</h3>
                  {p.description && <p className="text-[color:var(--text-2)] text-sm mt-1 flex-1">{p.description}</p>}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-gold font-display text-3xl">{p.price_eur.toFixed(2)}€</span>
                    <button onClick={() => redeem(p)} disabled={!allowed}
                      className="btn-primary !py-2 !px-4 text-sm" data-testid={`redeem-${p.prize_id}`}>
                      {user ? (allowed ? "Resgatar" : "Saldo insuficiente") : "Entrar"}
                    </button>
                  </div>
                  {user && !allowed && (
                    <p className="text-xs text-[color:var(--text-3)] mt-2 flex items-center gap-1">
                      <ShieldCheck size={12}/> Faltam {(p.price_eur - balance).toFixed(2)}€
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {prizes.length === 0 && <p className="text-[color:var(--text-2)]">Em breve mais prémios.</p>}
        </div>
      )}

      {redeeming && (
        <RedeemModal prize={redeeming} balance={balance} onClose={() => setRedeeming(null)} onConfirm={doRedeem} />
      )}
    </main>
  );
}

function RedeemModal({ prize, balance, onClose, onConfirm }) {
  const [method, setMethod] = useState("pickup");
  const [address, setAddress] = useState({ name: "", street: "", postal_code: "", city: "", phone: "" });
  // Portes: por omissão saem do saldo se chegar; se não chegar, a pessoa
  // pode escolher pagá-los à parte (mbway/iban/paypal/crypto).
  const [payShipping, setPayShipping] = useState("balance");
  const [paymentMethod, setPaymentMethod] = useState("mbway");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // resgate criado, se portes ficaram a pagar à parte

  const canCoverWithBalance = balance >= prize.price_eur + SHIPPING_FEE - 0.0001;
  const canCoverPrizeOnly = balance >= prize.price_eur - 0.0001;
  const addressComplete = Object.values(address).every(v => v.trim().length > 0);

  useEffect(() => {
    if (method === "shipping" && !canCoverWithBalance) setPayShipping("separate");
  }, [method, canCoverWithBalance]);

  const balanceDebit = prize.price_eur + (method === "shipping" && payShipping === "balance" ? SHIPPING_FEE : 0);

  const submit = async () => {
    if (method === "shipping" && !addressComplete) { toast.error("Preenche a morada completa"); return; }
    if (method === "shipping" && payShipping === "separate" && !canCoverPrizeOnly) {
      toast.error(`Saldo insuficiente até para o prémio (faltam ${(prize.price_eur - balance).toFixed(2)}€)`);
      return;
    }
    setSubmitting(true);
    try {
      const data = await onConfirm(prize, {
        delivery_method: method,
        address: method === "shipping" ? address : null,
        shipping_payment_method: method === "shipping" && payShipping === "separate" ? paymentMethod : null,
      });
      if (data.shipping_payment?.status === "pending") {
        setResult(data);
      } else {
        toast.success("Prémio resgatado! Receberás contacto em breve.");
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a resgatar");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ShippingPaymentModal redemption={result} onClose={onClose} />;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="glass p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="redeem-modal">
        <h3 className="font-display text-2xl mb-1">{prize.name}</h3>
        <p className="text-[color:var(--text-2)] text-sm mb-4">Como queres receber?</p>

        <div className="space-y-2 mb-4">
          <label className="flex items-center gap-3 p-3 rounded-md border border-[color:var(--border)] cursor-pointer">
            <input type="radio" checked={method === "pickup"} onChange={() => setMethod("pickup")} />
            <span className="flex-1">Levantamento presencial</span>
            <span className="text-gold">Grátis</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-md border border-[color:var(--border)] cursor-pointer">
            <input type="radio" checked={method === "shipping"} onChange={() => setMethod("shipping")} />
            <span className="flex-1">Envio para casa</span>
            <span className="text-gold">+{SHIPPING_FEE.toFixed(2)}€</span>
          </label>
        </div>

        {method === "shipping" && (
          <>
            <div className="space-y-2 mb-4">
              <input className="input" placeholder="Nome" value={address.name} onChange={e => setAddress({ ...address, name: e.target.value })} data-testid="address-name" />
              <input className="input" placeholder="Morada" value={address.street} onChange={e => setAddress({ ...address, street: e.target.value })} data-testid="address-street" />
              <div className="flex gap-2">
                <input className="input" placeholder="Código-postal" value={address.postal_code} onChange={e => setAddress({ ...address, postal_code: e.target.value })} data-testid="address-postal" />
                <input className="input" placeholder="Localidade" value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} data-testid="address-city" />
              </div>
              <input className="input" placeholder="Telemóvel" value={address.phone} onChange={e => setAddress({ ...address, phone: e.target.value })} data-testid="address-phone" />
            </div>

            <div className="mb-4">
              <p className="text-[color:var(--text-2)] text-sm mb-2">Como pagas os {SHIPPING_FEE.toFixed(2)}€ de portes?</p>
              <div className="space-y-2">
                <label className={`flex items-center gap-3 p-3 rounded-md border border-[color:var(--border)] ${canCoverWithBalance ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
                  <input type="radio" checked={payShipping === "balance"} disabled={!canCoverWithBalance}
                    onChange={() => setPayShipping("balance")} data-testid="shipping-pay-balance" />
                  <span className="flex-1">Do saldo</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-md border border-[color:var(--border)] cursor-pointer">
                  <input type="radio" checked={payShipping === "separate"} onChange={() => setPayShipping("separate")} data-testid="shipping-pay-separate" />
                  <span className="flex-1">Pago à parte (MBWay/IBAN/PayPal/Crypto)</span>
                </label>
              </div>
              {!canCoverWithBalance && (
                <p className="text-xs text-[color:var(--text-3)] mt-2">
                  Saldo não chega para prémio + portes (faltam {(prize.price_eur + SHIPPING_FEE - balance).toFixed(2)}€) — os portes ficam para pagar à parte.
                </p>
              )}
              {payShipping === "separate" && (
                <select className="input mt-2" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} data-testid="shipping-payment-method">
                  {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              )}
            </div>
          </>
        )}

        <div className="flex items-center justify-between mb-4">
          <span className="text-[color:var(--text-2)] text-sm">Total a debitar do saldo</span>
          <span className="font-display text-2xl text-gold">{balanceDebit.toFixed(2)}€</span>
        </div>

        <div className="flex gap-3">
          <button className="btn-secondary flex-1" onClick={onClose}>Cancelar</button>
          <button className="btn-primary flex-1" onClick={submit} disabled={submitting} data-testid="confirm-redeem">
            {submitting ? "A processar…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShippingPaymentModal({ redemption, onClose }) {
  const [settings, setSettings] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  useEffect(() => { api.get("/settings/public").then(r => setSettings(r.data)); }, []);

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado"); };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/redemptions/${redemption.redemption_id}/shipping-proof`, form);
      toast.success("Comprovativo enviado! Aguarda a confirmação do admin.");
      setUploaded(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a enviar comprovativo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const sp = redemption.shipping_payment;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="glass p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="shipping-payment-modal">
        <h3 className="font-display text-2xl mb-1">Prémio resgatado!</h3>
        <p className="text-[color:var(--text-2)] text-sm mb-4">
          Falta só pagar os {sp.amount.toFixed(2)}€ de portes via {METHOD_LABEL[sp.method]}.
        </p>

        {!settings ? <p className="text-gold">A carregar…</p> : (
          <div className="border-t border-[color:var(--border)] pt-4 mb-4">
            {sp.method === "mbway" && <PayLine label="MBWay" value={settings.mbway_phone} onCopy={copy} />}
            {sp.method === "paypal" && <PayLine label="PayPal" value={settings.paypal_email} onCopy={copy} />}
            {sp.method === "transfer" && <PayLine label="IBAN" value={settings.iban} onCopy={copy} />}
            {sp.method === "crypto" && Object.entries(settings.crypto_wallets || {}).map(([k, v]) => (
              <PayLine key={k} label={k} value={v} onCopy={copy} />
            ))}
            <p className="text-[color:var(--text-3)] text-xs mt-3">
              Indica a referência do resgate na descrição: <span className="text-gold">#{redemption.redemption_id}</span>
            </p>
          </div>
        )}

        <label className="btn-primary flex items-center justify-center gap-2 cursor-pointer w-full mb-3" data-testid="upload-shipping-proof-label">
          <Upload size={16}/> {uploading ? "A enviar…" : (uploaded ? "Comprovativo enviado ✓" : "Upload comprovativo")}
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
            onChange={uploadProof} disabled={uploading} data-testid="upload-shipping-proof-input" />
        </label>

        <button className="btn-secondary w-full" onClick={onClose}>Fechar</button>
      </div>
    </div>
  );
}

function PayLine({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-md border border-[color:var(--border)] mb-2">
      <span className="text-gold font-semibold w-20">{label}</span>
      <span className="flex-1 font-mono text-sm break-all">{value}</span>
      <button onClick={() => onCopy(value)} className="text-[color:var(--text-2)] hover:text-gold" title="Copiar">Copiar</button>
    </div>
  );
}
